import React from "react";
import "./Footer.css";
import { useLocalContext } from "../../context/LocalContext";
import { useNavigate } from "react-router-dom";
import { useProduct } from "../../context/ProductContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faShieldAlt,
  faBolt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const Footer = () => {
  const { webinfo = {} } = useLocalContext();
  const { setSelectedAntiv } = useProduct();
  const navigate = useNavigate();

  const categories = [
    { name: "McAfee", route: "mcafee" },
    { name: "Norton", route: "norton" },
    { name: "Avast", route: "avast" },
    { name: "Kaspersky", route: "kaspersky" },
    { name: "AVG", route: "avg" },
    { name: "Bitdefender", route: "bitdefender" },
    { name: "TotalAV", route: "totalav" },
  ];

  const year = new Date().getFullYear();

  return (
    <footer className="ft ft-light">
      {/* top service strip */}
      <div className="ft-strip" role="list" aria-label="Service summary">
        <div className="ft-strip-item" role="listitem">
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>Digital security licenses</span>
        </div>
        <div className="ft-strip-item" role="listitem">
          <FontAwesomeIcon icon={faBolt} />
          <span>Email delivery with setup info</span>
        </div>
        <div className="ft-strip-item" role="listitem">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>Support from the software vendor</span>
        </div>
      </div>

      <div className="ft-inner">
        {/* Brand / short explanation */}
        <section className="ft-col ft-brand" aria-labelledby="ft-brand-title">
          <img src={webinfo.logo} alt="" />
          <p className="ft-tagline">
            Independent online store for popular antivirus and security tools.
            We focus on clear information, digital delivery, and simple
            activation guidance before you start using the software.
          </p>

          <button
            className="ft-cta"
            type="button"
            onClick={() => navigate("/latest-products")}
            aria-label="Browse all security plans"
          >
            Browse security plans
          </button>

          <p className="ft-note">
            We do not develop the software sold here. Features, availability,
            and device support are set by each vendor and can change over time.
            Always review the latest plan details before purchasing.
          </p>
        </section>

        {/* Plans by brand */}
        <nav className="ft-col" aria-labelledby="ft-products-title">
          <h3 id="ft-products-title" className="ft-heading">
            Plans by brand
          </h3>
          <ul className="ft-links">
            {categories.map((cat) => (
              <li key={cat.route}>
                <button
                  className="ft-link"
                  type="button"
                  onClick={() => {
                    setSelectedAntiv(cat.name);
                    navigate(`/${cat.route}`);
                  }}
                >
                  {cat.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Help & info */}
        <nav className="ft-col" aria-labelledby="ft-help-title">
          <h3 id="ft-help-title" className="ft-heading">
            Help &amp; information
          </h3>
          <ul className="ft-links">
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/about")}
              >
                About this store
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Contact &amp; questions
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/whychooseus")}
              >
                How we explain plans
              </button>
            </li>
          </ul>
        </nav>

        {/* Policies / legal */}
        <nav className="ft-col" aria-labelledby="ft-legal-title">
          <h3 id="ft-legal-title" className="ft-heading">
            Policies
          </h3>
          <ul className="ft-links">
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/disclaimer")}
              >
                Store disclaimer
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/privacy-policy")}
              >
                Privacy &amp; data
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/terms-and-conditions")}
              >
                Terms of use
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/refund-policy")}
              >
                Refund &amp; cancellation
              </button>
            </li>
            <li>
              <button
                className="ft-link"
                type="button"
                onClick={() => navigate("/shipping-policy")}
              >
                Delivery information
              </button>
            </li>
          </ul>
        </nav>

        {/* Contact details */}
        <address
          className="ft-col ft-contact"
          aria-labelledby="ft-contact-title"
        >
          <h3 id="ft-contact-title" className="ft-heading">
            Contact
          </h3>

          <p className="ft-contact-line">
            <FontAwesomeIcon icon={faEnvelope} />
            <a href={`mailto:${webinfo?.email || ""}`}>
              {webinfo?.email || "support@example.com"}
            </a>
          </p>

          <p className="ft-contact-line">
            <FontAwesomeIcon icon={faPhone} />
            <a href={`tel:${webinfo?.phonecall || webinfo?.phone || ""}`}>
              {webinfo?.phone || "Call us for plan guidance"}
            </a>
          </p>

          {webinfo?.address && (
            <p className="ft-contact-line">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{webinfo.address}</span>
            </p>
          )}
        </address>
      </div>

      {/* bottom line */}
      <div className="ft-bottom" role="contentinfo">
        <p className="ft-copy">
          © {year} {webinfo?.name || "Security License Store"}. All rights
          reserved.
        </p>
        <p className="ft-trademark">
          Product names, brands, and logos are trademarks of their respective
          owners. They are used here only to identify the software offered for
          sale.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
